import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";

/**
 * PWA install prompt — shown when the browser fires `beforeinstallprompt`
 * (Chrome / Edge / Samsung). Suppressed if:
 *   - User has already installed (display-mode: standalone)
 *   - User has dismissed in the past 14 days (localStorage flag)
 *
 * iOS Safari doesn't fire beforeinstallprompt — instead we show a manual
 * "Add to Home Screen" hint after a delay when running on iOS Safari.
 */

const DISMISS_KEY = "tm-install-dismissed-at";
const DISMISS_TTL_MS = 14 * 24 * 60 * 60 * 1000;

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

function isIosSafari(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  const isIos = /iPhone|iPad|iPod/.test(ua);
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
  return isIos && isSafari;
}

function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    (window.navigator as any).standalone === true
  );
}

function recentlyDismissed(): boolean {
  if (typeof localStorage === "undefined") return false;
  const raw = localStorage.getItem(DISMISS_KEY);
  if (!raw) return false;
  const ts = parseInt(raw, 10);
  if (!Number.isFinite(ts)) return false;
  return Date.now() - ts < DISMISS_TTL_MS;
}

export default function InstallPrompt() {
  const [event, setEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [iosHintVisible, setIosHintVisible] = useState(false);

  useEffect(() => {
    if (isStandalone() || recentlyDismissed()) return;

    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      setEvent(e as BeforeInstallPromptEvent);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS Safari: no native prompt event. Show hint after 5s on landing.
    if (isIosSafari()) {
      const t = setTimeout(() => setIosHintVisible(true), 5_000);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      };
    }
    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
    setEvent(null);
    setIosHintVisible(false);
  };

  const install = async () => {
    if (!event) return;
    await event.prompt();
    const choice = await event.userChoice;
    if (choice.outcome === "dismissed") dismiss();
    setEvent(null);
  };

  if (event) {
    return (
      <div className="fixed bottom-20 left-4 right-4 z-40 sm:left-auto sm:right-4 sm:max-w-sm">
        <div className="bg-[#1a1a2e] border border-white/15 rounded-2xl p-4 shadow-xl flex items-start gap-3">
          <Download className="w-5 h-5 text-[#C9A96E] flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm font-semibold">Install Time Miners</p>
            <p className="text-white/60 text-xs mt-0.5">Get the app on your home screen — faster, offline-ready.</p>
            <div className="flex gap-2 mt-3">
              <button onClick={install} className="bg-[#C9A96E] hover:bg-[#d4b87a] text-black text-xs font-bold px-3 py-1.5 rounded-lg">
                Install
              </button>
              <button onClick={dismiss} className="text-white/50 hover:text-white text-xs px-3 py-1.5">
                Not now
              </button>
            </div>
          </div>
          <button onClick={dismiss} aria-label="Close" className="text-white/40 hover:text-white/80">
            <X size={14} />
          </button>
        </div>
      </div>
    );
  }

  if (iosHintVisible) {
    return (
      <div className="fixed bottom-20 left-4 right-4 z-40">
        <div className="bg-[#1a1a2e] border border-white/15 rounded-2xl p-4 shadow-xl flex items-start gap-3">
          <Download className="w-5 h-5 text-[#C9A96E] flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm font-semibold">Add Time Miners to your Home Screen</p>
            <p className="text-white/70 text-xs mt-1 leading-snug">
              Tap the Share button{" "}
              <span aria-hidden className="inline-block px-1 py-0.5 bg-white/10 rounded">⎙</span>
              {" "}then{" "}
              <strong className="text-white">Add to Home Screen</strong>.
            </p>
          </div>
          <button onClick={dismiss} aria-label="Close" className="text-white/40 hover:text-white/80">
            <X size={14} />
          </button>
        </div>
      </div>
    );
  }

  return null;
}
